import React, { useState, useEffect, useContext, forwardRef } from "react";
import "../Admin.css";
import "./AdminBookings.css";

import Spinner from "../../../components/Spinner";

import { getData, postData } from "../../../api";
import { formatToronto, calendarBoundary, torontoToday } from "../../../utils/TorontoTime";
import { useNavigate } from "react-router-dom";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import NoContent from "../../../components/NoContent";
import Selector from "../../../components/Selector";
import { WithInPickUpArea } from "../../../constant/Config";
import { AppContext, createBookingFilters } from "../../../context/AppContext";

const BOOKING_STATUSES = [
  "All",
  "Pending",
  "Confirmed",
  "Cancelled by Admin",
  "Cancelled by User",
];

const CustomDatepicker = forwardRef(({ value, onClick }, ref) => (
  <button
    type="button"
    className="example-custom-input"
    onClick={onClick}
    ref={ref}
    aria-label="Booking date"
  >
    {value || "Select date"}
  </button>
));

const AdminBookings = () => {
  const navigator = useNavigate();
  const [loading, setloading] = useState(true);
  const [bookings, setbookings] = useState([]);
  const [rides, setrides] = useState([]);
  const { bookingFilters, setBookingFilters } = useContext(AppContext);
  const { date, dateFilterEnabled, rideId, status } = bookingFilters;
  const startTime = calendarBoundary(dateFilterEnabled ? date : torontoToday()).getTime();
  const endTime = dateFilterEnabled ? calendarBoundary(date, true).getTime() : null;
  const filterList = bookings
    .filter((booking) => {
      const scheduledTime = new Date(booking.ScheduledToTime).getTime();
      return scheduledTime >= startTime &&
        (endTime === null || scheduledTime <= endTime) &&
        (!rideId || rideId === booking.ride_id?._id) &&
        (status === "All" || booking.status === status);
    })
    .sort((a, b) => new Date(a.ScheduledToTime).getTime() - new Date(b.ScheduledToTime).getTime());

  // clear filter
  const handleClearFilter = () => {
    setBookingFilters(createBookingFilters());
  };

  // api calls
  useEffect(() => {
    let active = true;
    const apiCall = async () => {
      setloading(true);
      const [rides, bookings] = await Promise.all([
        getData("/ride/rides", false),
        postData("/booking/bookings", {
          startTime: new Date(startTime),
          ...(endTime === null ? {} : { endTime: new Date(endTime) }),
        }),
      ]);
      if (!active) return;
      setrides(
        Array.isArray(rides)
          ? rides.map((item) => ({
              id: item._id,
              item: item.rideName,
            }))
          : [],
      );
      setbookings(Array.isArray(bookings) ? bookings : []);
      setloading(false);
    };
    apiCall();
    return () => {
      active = false;
    };
  }, [startTime, endTime]);

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <div className="admin bookings">
          <div className="filter">
            <div
              className="status-filter"
              role="group"
              aria-label="Booking status"
            >
              <span>Status</span>
              <Selector
                key={status}
                title="All"
                allowDeselect={false}
                items={BOOKING_STATUSES.map((value) => ({
                  id: value,
                  item: value,
                }))}
                initialSelection={[{ id: status, item: status }]}
                selectedItem={(selection) =>
                  setBookingFilters((filters) => ({
                    ...filters,
                    status: selection[0]?.id || "All",
                  }))
                }
              />
            </div>
            <div className="ride-filter" role="group" aria-label="Booking ride">
              <span>Ride</span>
              <Selector
                key={rideId || "all-rides"}
                title={"Select ride"}
                items={rides}
                initialSelection={rides.filter((ride) => ride.id === rideId)}
                selectedItem={(e) => {
                  setBookingFilters((filters) => ({
                    ...filters,
                    rideId: e.length > 0 ? e[0].id : null,
                  }));
                }}
              />
            </div>
            <div
              className="date-filter"
              role="group"
              aria-label="Booking date filter"
            >
              <span>Date{!dateFilterEnabled && " (today onward)"}</span>
              <DatePicker
                className="date-picker"
                selected={date}
                // onSelect also fires when choosing the already displayed day.
                onSelect={(selectedDate) => {
                  if (selectedDate)
                    setBookingFilters((filters) => ({
                      ...filters,
                      date: selectedDate,
                      dateFilterEnabled: true,
                    }));
                }}
                dateFormat="MMMM d, yyyy"
                customInput={<CustomDatepicker />}
              />
            </div>
            <button className="clear" onClick={() => handleClearFilter()}>
              Clear Filters
            </button>
          </div>
          {filterList.length > 0 ? (
            filterList.map((booking) => (
              <div
                key={booking._id}
                className="list-content"
                onClick={() => navigator(`/admin/bookings/${booking._id}`)}
              >
                <div className="main-container">
                  <div>
                    <div className="name">Name: {booking.user_id.name}</div>
                    <div>Phone: {booking.user_id.phone}</div>
                  </div>
                  <div>
                    <div className={`status`}>
                      Status:{" "}
                      <span className={`${booking.status}`}>
                        {booking.status}
                      </span>
                    </div>

                    <div className="price">
                      Price: <span>${booking.price}</span>
                    </div>
                  </div>
                </div>
                <div className="ride-name">
                  Ride Name: {booking.ride_id?.rideName}
                </div>
                <div className="date">
                  Scheduled for:{" "}
                  {formatToronto(
                    booking.ScheduledToTime,
                    "DD/MM/YYYY  hh:mm A",
                  )}
                </div>
                <div className="from">
                  Pickup:{" "}
                  {booking.from.location_id._id === WithInPickUpArea
                    ? booking.from.customLocation
                    : booking.from.location_id.location}{" "}
                  <span className="city-name">{` (${booking.from.city_id.city})`}</span>
                </div>
                <div className="to">
                  Dropoff:{" "}
                  {booking.to.location_id._id === WithInPickUpArea
                    ? booking.to.customLocation
                    : booking.to.location_id.location}
                  <span className="city-name">
                    {" "}
                    {` (${booking.to.city_id.city})`}
                  </span>
                </div>

                {booking.driver && (
                  <span className="driver">
                    Driver: <span> {booking.driver.name}</span>
                  </span>
                )}
              </div>
            ))
          ) : (
            <NoContent content={"No Bookings Available"} />
          )}
        </div>
      )}
    </>
  );
};

export default AdminBookings;
